import React, { useState, useEffect } from 'react';
import { getApiErrorMessage } from '../lib/apiError';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle, Cancel, Lock as LockIcon } from '@mui/icons-material';
import apiClient from '../lib/axios';
import { AuthLayout } from '../components/AuthLayout';

interface OnboardingUser {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

export const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [userInfo, setUserInfo] = useState<OnboardingUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordRequirements = [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'One uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'One special character', met: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
    { label: 'Passwords match', met: password === confirmPassword && password.length > 0 },
  ];

  const isPasswordValid = passwordRequirements.every((r) => r.met);

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided');
      setLoading(false);
      return;
    }

    const validateToken = async () => {
      try {
        const response = await apiClient.get(`/auth/onboarding/validate/${token}`);
        setUserInfo(response.data.data);
      } catch (err: any) {
        setError(getApiErrorMessage(err, 'Invalid or expired invitation link'));
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isPasswordValid) return;

    setSubmitting(true);
    setError('');

    try {
      await apiClient.post('/auth/onboarding', { token, newPassword: password });
      setSuccess('Account activated successfully! Redirecting to login...');
      setTimeout(() => {
        navigate('/login', { state: { message: 'Account activated. Please log in.', email: userInfo?.email } });
      }, 2000);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to complete setup'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout showBranding={false}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Set Up Your Account" subtitle="MOH SMS Reminder System" maxWidth={500}>
      {error && !userInfo && (
        <>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Button fullWidth variant="outlined" onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
      )}

      {userInfo && !success && (
        <>
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 2, mb: 3 }}>
            <Typography variant="body2">
              <strong>{userInfo.firstName} {userInfo.lastName}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">{userInfo.email}</Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <TextField
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={submitting}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <List dense sx={{ mb: 2 }}>
              {passwordRequirements.map((req, i) => (
                <ListItem key={i} disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {req.met ? (
                      <CheckCircle fontSize="small" color="success" />
                    ) : (
                      <Cancel fontSize="small" color="disabled" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={req.label}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: req.met ? 'success.main' : 'text.secondary',
                    }}
                  />
                </ListItem>
              ))}
            </List>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={!isPasswordValid || submitting}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : 'Activate Account'}
            </Button>
          </form>
        </>
      )}
    </AuthLayout>
  );
};
